/* eslint-disable dot-notation */
import log from "loglevel";
import TagManager from "react-gtm-module";

let isGtmActive = false;

type Props = {
    gtmId: string;
    auth: string;
    preview: string;
};

export const init = ({ gtmId, auth, preview }: Props): void => {
    if (gtmId) {
        TagManager.initialize({
            gtmId,
            auth,
            preview,
        });

        isGtmActive = true;
    }
};

export type TMEvent =
    | "add_broker"
    | "add_broker_search"
    | "add_broker_confirmation"
    | "office_segmentation"
    | "cross_platform_tracking"
    | "address_change_started"
    | "address_change_sent"
    | "insurance_card_begin"
    | "insurance_card_sent"
    | "sign_up"
    | "login"
    | "error"
    | "logout"
    | "change_password_begin"
    | "change_password_completed"
    | "document_signed"
    | "document_refused"
    | "add_family_member_started"
    | "add_family_member_sent"
    | "view_list"
    | "view_item"
    | "broker_info"
    | "view_personal_details"
    | "edit_personal_details_begin"
    | "edit_personal_details_sent"
    | "view_relation_details"
    | "edit_relation_details_begin"
    | "edit_relation_details_sent"
    | "feedback_survey";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const pushEvent = (event: TMEvent, data: any = {}): void => {
    if (!isGtmActive) return;
    log.info(`Tag manager event: ${event}, data: ${JSON.stringify(data)}`);

    window["dataLayer"].push({
        event,
        ...data,
    });
};

export type TMViewListId =
    | "relatedActivities"
    | "contract"
    | "insuredGood"
    | "claim"
    | "invoices"
    | "request"
    | "relatedDocument"
    | "document"
    | "conversation"
    | "documentToSign"
    | "documentContract"
    | "documentClaim"
    | "documentInsuredgood";

export type TMViewListName =
    | "Related Activities"
    | "Contracts"
    | "Insured Goods"
    | "Claims"
    | "Invoices"
    | "Requests"
    | "Related Documents"
    | "Documents"
    | "Conversations"
    | "Documents to Sign"
    | "Contracts Documents"
    | "Claims Documents"
    | "Insured Goods Documents";

export type TMViewItemId =
    | "contract"
    | "insuredGood"
    | "claim"
    | "invoices"
    | "request"
    | "document"
    | "conversation"
    | "documentToSign"
    | "documentRequest";

export type TMViewItemName =
    | "Contracts"
    | "Insured Goods"
    | "Claims"
    | "Invoices"
    | "Requests"
    | "Documents"
    | "Conversations"
    | "Documents to Sign"
    | "Requests Documents";

export type TMFormEditId =
    | "general_information"
    | "driver_license_and_id"
    | "finance"
    | "commercial_delegate"
    | "contact"
    | "profession"
    | "administration";

export type TMFormEditName =
    | "General Information"
    | "Driver License and Id"
    | "Finance"
    | "Commercial Delegate"
    | "Contact"
    | "Profession"
    | "Administration";

export const pushLogoutEvent = (): void => {
    pushEvent("logout");
};

export const pushLoginEvent = (method: "itsme" | "email"): void => {
    pushEvent("login", { method });
};

export const pushSignUpEvent = (method: "itsme" | "email", mode?: "pull" | "push"): void => {
    pushEvent("sign_up", { method, mode });
};

export const pushUserPropertyUserId = (userId: string = null): void => {
    pushEvent("cross_platform_tracking", { value: userId });
};

export const pushUserPropertyOfficeId = (officeId: string = null): void => {
    pushEvent("office_segmentation", { value: officeId });
};

export const pushBrokerInfoEvent = (): void => {
    pushEvent("broker_info");
};

export const pushErrorEvent = (error: Error): void => {
    pushEvent("error", { value: error.message });
};

export const pushAddBrokerEvent = (): void => {
    pushEvent("add_broker");
};

export const pushAddBrokerSearchEvent = (): void => {
    pushEvent("add_broker_search");
};

export const pushAddBrokerConfirmationEvent = (): void => {
    pushEvent("add_broker_confirmation");
};

export const pushViewItemListEvent = (
    itemListId: TMViewListId,
    itemListName: TMViewListName,
    embedded = false,
): void => {
    pushEvent("view_list", { item_list_id: itemListId, item_list_name: itemListName, embedded });
};

export const pushViewItemEvent = (itemListId: TMViewItemId, itemListName: TMViewItemName): void => {
    pushEvent("view_item", { item_list_id: itemListId, item_list_name: itemListName });
};

export const pushInsuranceCardRequestBeginEvent = (): void => {
    pushEvent("insurance_card_begin");
};

export const pushInsuranceCardRequestSentEvent = (success = true): void => {
    pushEvent("insurance_card_sent", { success });
};

export const pushChangePasswordBeginEvent = (): void => {
    pushEvent("change_password_begin");
};

export const pushChangePasswordSentEvent = (): void => {
    pushEvent("change_password_completed");
};

export const pushAddFamilyMemberBeginEvent = (): void => {
    pushEvent("add_family_member_started");
};

export const pushAddFamilyMemberSentEvent = (success = true): void => {
    pushEvent("add_family_member_sent", { success });
};

export const pushAddressChangeBeginEvent = (): void => {
    pushEvent("address_change_started");
};

export const pushAddressChangeSentEvent = (success = true): void => {
    pushEvent("address_change_sent", { success });
};

export const pushDocumentSignedEvent = (success = true): void => {
    pushEvent("document_signed", { method: "checkbox", success });
};

export const pushDocumentRefusedEvent = (success = true): void => {
    pushEvent("document_refused", { success });
};

export const pushViewPersonalDetailsEvent = (): void => {
    pushEvent("view_personal_details");
};

export const pushEditPersonalDetailsBeginEvent = (formId: TMFormEditId, formName: TMFormEditName): void => {
    pushEvent("edit_personal_details_begin", { form_id: formId, form_name: formName });
};

export const pushEditPersonalDetailsSentEvent = (
    formId: TMFormEditId,
    formName: TMFormEditName,
    success = true,
): void => {
    pushEvent("edit_personal_details_sent", { form_id: formId, form_name: formName, success });
};

export const pushViewRelationsDetailsEvent = (): void => {
    pushEvent("view_relation_details");
};

export const pushEditRelationsDetailsBeginEvent = (formId: TMFormEditId, formName: TMFormEditName): void => {
    pushEvent("edit_relation_details_begin", { form_id: formId, form_name: formName });
};

export const pushEditRelationsDetailsSentEvent = (
    formId: TMFormEditId,
    formName: TMFormEditName,
    success = true,
): void => {
    pushEvent("edit_relation_details_sent", { form_id: formId, form_name: formName, success });
};

export const pushFeedbackSurveyResponse = (
    surveyId: string,
    questionId: string,
    responseId: string,
    info: string,
): void => {
    pushEvent("feedback_survey", {
        survey_id: surveyId,
        survey_question_id: questionId,
        survey_response_id: responseId,
        survey_response_info: info,
    });
};
